import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import api from '../api';

import ClientesList from './ClientesList';
import AssinaturasAdmin from './AssinaturasAdmin';
import AdicionaLicencas from './AdicionaLicencas';
import ListarLicencas from './ListarLicencas';
import AdicionaPagamentos from './AdicionaPagamentos';
import ListarPagamentos from './ListarPagamentos';
import PlanosAdmin from './PlanosAdmin';
import ResetSenha from './ResetSenha';
import Suporte from './Suporte';
import { useAuth } from '../auth';
import './estilodash.css';

function fmtMoney(v) {
  return Number(v || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtInt(v) {
  return Number(v || 0).toLocaleString('pt-BR');
}

function fmtDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('pt-BR');
}

function truncate(value, max = 90) {
  if (!value) return '—';
  const s = String(value);
  if (s.length <= max) return s;
  return `${s.slice(0, max)}...`;
}

function statusLabel(flag) {
  return String(flag) === '1' ? 'Ativo' : 'Inativo';
}

function boolResult(flag) {
  return Number(flag) === 1 ? 'OK' : 'Falha';
}

function getResultadoBadgeStyle(ok) {
  return {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 700,
    background: Number(ok) === 1 ? '#e8f7ee' : '#fdecec',
    color: Number(ok) === 1 ? '#166534' : '#991b1b',
    border: Number(ok) === 1 ? '1px solid #b7ebc6' : '1px solid #f5b7b7',
  };
}

function getStatusBadgeStyle(isActive) {
  return {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 700,
    background: isActive ? '#e8f7ee' : '#fff4db',
    color: isActive ? '#166534' : '#92400e',
    border: isActive ? '1px solid #b7ebc6' : '1px solid #f3d19c',
  };
}

function SectionCard({ title, subtitle, children, right }) {
  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: 16,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h3 style={{ margin: 0, color: '#443c56' }}>{title}</h3>
          {subtitle ? (
            <p style={{ margin: '6px 0 0 0', color: '#6b7280', fontSize: 14 }}>
              {subtitle}
            </p>
          ) : null}
        </div>
        {right ? <div>{right}</div> : null}
      </div>
      {children}
    </div>
  );
}

function KpiCard({ title, value, small }) {
  return (
    <div
      className="kpi-card"
      style={{
        background: '#ffffff',
        color: '#111827',
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      <h3 style={{ color: '#6b7280' }}>{title}</h3>
      <p style={{ color: '#111827' }}>{value}</p>
      {small ? <small style={{ color: '#6b7280' }}>{small}</small> : null}
    </div>
  );
}

function DashboardHome({
  resumo,
  authLogs,
  carregandoResumo,
  erroResumo,
  onReload,
}) {
  const clientes = resumo?.clientes || {};
  const assinaturas = resumo?.assinaturas || {};
  const licencas = resumo?.licencas || {};
  const pagamentos = resumo?.pagamentos || {};
  const auth = resumo?.auth || {};
  const novosPendentes = Array.isArray(resumo?.novos_pendentes)
    ? resumo.novos_pendentes
    : [];
  const ultimosLogins = Array.isArray(resumo?.ultimos_logins)
    ? resumo.ultimos_logins
    : [];
  const topFalhas = Array.isArray(resumo?.top_falhas)
    ? resumo.top_falhas
    : [];

  const ultimosLogsRobo = authLogs.filter(
    (l) =>
      l.origem_log === 'novo_licenciamento' ||
      l.origem_log === 'legado' ||
      l.origem_log === 'robo'
  );

  const ultimosLogsUsuario = authLogs.filter((l) => l.origem_log === 'usuario');

  return (
    <div className="dashboard-home" style={{ color: '#111827' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div>
          <h2 style={{ marginBottom: 6 }}>Visão Geral</h2>
          <p
            className="dashboard-home-subtitle"
            style={{ color: '#6b7280', margin: 0 }}
          >
            Painel operacional para clientes, ativações, licenciamento e auditoria de autenticação.
          </p>
        </div>

        <button type="button" onClick={onReload}>
          Atualizar dados
        </button>
      </div>

      {erroResumo ? (
        <div
          style={{
            background: '#fdecec',
            color: '#7f1d1d',
            border: '1px solid #f5b7b7',
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
          }}
        >
          {erroResumo}
        </div>
      ) : null}

      {carregandoResumo ? (
        <p>Carregando visão geral...</p>
      ) : (
        <>
          <div className="kpi-container">
            <KpiCard
              title="Clientes"
              value={fmtInt(clientes.total_clientes)}
              small={`Ativos: ${fmtInt(clientes.clientes_ativos)} | Desativados: ${fmtInt(clientes.clientes_desativados)}`}
            />
            <KpiCard
              title="Novos pendentes"
              value={fmtInt(clientes.novos_desativados_7d)}
              small="Cadastros dos últimos 7 dias ainda desativados"
            />
            <KpiCard
              title="Nunca logaram"
              value={fmtInt(clientes.nunca_logaram)}
              small="Usuários sem login registrado"
            />
            <KpiCard
              title="Assinaturas ativas"
              value={fmtInt(assinaturas.assinaturas_ativas)}
              small={`Slots ativos: ${fmtInt(assinaturas.slots_ativos)}`}
            />
            <KpiCard
              title="Licenças ativas"
              value={fmtInt(licencas.licencas_ativas)}
              small={`EA ${fmtInt(licencas.lic_ea)} | Bias ${fmtInt(licencas.lic_bias)} | Ragnarok ${fmtInt(licencas.lic_ragnarok)}`}
            />
            <KpiCard
              title="Receita mês atual"
              value={`R$ ${fmtMoney(pagamentos.receita_mes_atual)}`}
              small={`Receita total: R$ ${fmtMoney(pagamentos.receita_total)}`}
            />
            <KpiCard
              title="Auth robôs (7d)"
              value={fmtInt(auth.auth_robo_total)}
              small={`OK: ${fmtInt(auth.auth_sucesso)} | Falhas: ${fmtInt(auth.auth_falha)}`}
            />
            <KpiCard
              title="Auth usuários (7d)"
              value={fmtInt(auth.auth_usuario_total)}
              small={`Isentos: ${fmtInt(clientes.clientes_isentos)}`}
            />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 1fr',
              gap: 16,
              marginTop: 20,
            }}
          >
            <SectionCard
              title="Pendentes de ativação"
              subtitle="Novos usuários cadastrados que seguem desativados por padrão."
            >
              <table className="clientes-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Criado em</th>
                    <th>Último login</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {novosPendentes.length === 0 ? (
                    <tr>
                      <td colSpan="6">Nenhum cliente pendente no momento.</td>
                    </tr>
                  ) : (
                    novosPendentes.map((c) => (
                      <tr key={c.id}>
                        <td>{c.id}</td>
                        <td>{c.nome || '—'}</td>
                        <td>{c.email || '—'}</td>
                        <td>{fmtDateTime(c.created_on)}</td>
                        <td>{fmtDateTime(c.last_login)}</td>
                        <td>
                          <span style={getStatusBadgeStyle(String(c.status) === '1')}>
                            {statusLabel(c.status)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </SectionCard>

            <SectionCard
              title="Últimos logins de usuários"
              subtitle="Útil para verificar atividade recente e contas sem uso."
            >
              <table className="clientes-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Último login</th>
                  </tr>
                </thead>
                <tbody>
                  {ultimosLogins.length === 0 ? (
                    <tr>
                      <td colSpan="5">Nenhum login encontrado.</td>
                    </tr>
                  ) : (
                    ultimosLogins.map((u) => (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>{u.nome || '—'}</td>
                        <td>{u.email || '—'}</td>
                        <td>
                          <span style={getStatusBadgeStyle(String(u.status) === '1')}>
                            {statusLabel(u.status)}
                          </span>
                        </td>
                        <td>{fmtDateTime(u.last_login)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </SectionCard>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              marginTop: 16,
            }}
          >
            <SectionCard
              title="Autenticação dos robôs"
              subtitle="Resumo dos últimos 7 dias para a migração do novo licenciamento."
            >
              <table className="clientes-table">
                <thead>
                  <tr>
                    <th>Robô</th>
                    <th>Sucesso</th>
                    <th>Falha</th>
                    <th>Licenças ativas</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Quantum Trades EA</td>
                    <td>{fmtInt(auth.auth_ea_ok)}</td>
                    <td>{fmtInt(auth.auth_ea_fail)}</td>
                    <td>{fmtInt(licencas.lic_ea)}</td>
                  </tr>
                  <tr>
                    <td>QuantumTrades Bias</td>
                    <td>{fmtInt(auth.auth_bias_ok)}</td>
                    <td>{fmtInt(auth.auth_bias_fail)}</td>
                    <td>{fmtInt(licencas.lic_bias)}</td>
                  </tr>
                  <tr>
                    <td>QuantumTrades Ragnarok</td>
                    <td>{fmtInt(auth.auth_ragnarok_ok)}</td>
                    <td>{fmtInt(auth.auth_ragnarok_fail)}</td>
                    <td>{fmtInt(licencas.lic_ragnarok)}</td>
                  </tr>
                </tbody>
              </table>
            </SectionCard>

            <SectionCard
              title="Motivos de falha"
              subtitle="Principais falhas recentes de autenticação para atacar antes da virada."
            >
              <table className="clientes-table">
                <thead>
                  <tr>
                    <th>Motivo</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {topFalhas.length === 0 ? (
                    <tr>
                      <td colSpan="2">Nenhuma falha recente encontrada.</td>
                    </tr>
                  ) : (
                    topFalhas.map((item, idx) => (
                      <tr key={`${item.motivo}-${idx}`}>
                        <td>{item.motivo || 'Sem motivo'}</td>
                        <td>{fmtInt(item.total)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </SectionCard>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 16,
              marginTop: 16,
            }}
          >
            <SectionCard
              title="Últimos logs de robôs"
              subtitle="Conta, IP e user agent para auditoria operacional."
              right={
                <span style={{ color: '#6b7280', fontSize: 12 }}>
                  Mostrando até {ultimosLogsRobo.length} registros
                </span>
              }
            >
              <table className="clientes-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Robô</th>
                    <th>Conta</th>
                    <th>Cliente</th>
                    <th>Resultado</th>
                    <th>IP</th>
                    <th>User Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {ultimosLogsRobo.length === 0 ? (
                    <tr>
                      <td colSpan="7">Nenhum log de robô carregado.</td>
                    </tr>
                  ) : (
                    ultimosLogsRobo.map((l) => (
                      <tr key={l.id}>
                        <td>{fmtDateTime(l.created_at)}</td>
                        <td>{l.produto_nome || l.produto_codigo || l.robo_codigo || '—'}</td>
                        <td>{l.conta || '—'}</td>
                        <td>
                          {l.cliente_nome || '—'}
                          <br />
                          <small>{l.cliente_email || ''}</small>
                        </td>
                        <td>
                          <span style={getResultadoBadgeStyle(l.sucesso)}>
                            {boolResult(l.sucesso)}
                          </span>
                        </td>
                        <td title={l.ip || ''}>{truncate(l.ip, 40)}</td>
                        <td title={l.user_agent || ''}>{truncate(l.user_agent, 80)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </SectionCard>

            <SectionCard
              title="Últimos logs de usuários"
              subtitle="Eventos de autenticação de usuários para acompanhamento do painel."
              right={
                <span style={{ color: '#6b7280', fontSize: 12 }}>
                  Mostrando até {ultimosLogsUsuario.length} registros
                </span>
              }
            >
              <table className="clientes-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Cliente</th>
                    <th>Email</th>
                    <th>Resultado</th>
                    <th>Motivo</th>
                    <th>IP</th>
                    <th>User Agent</th>
                    <th>Endpoint</th>
                  </tr>
                </thead>
                <tbody>
                  {ultimosLogsUsuario.length === 0 ? (
                    <tr>
                      <td colSpan="8">Nenhum log de usuário carregado.</td>
                    </tr>
                  ) : (
                    ultimosLogsUsuario.map((l) => (
                      <tr key={l.id}>
                        <td>{fmtDateTime(l.created_at)}</td>
                        <td>{l.cliente_nome || '—'}</td>
                        <td>{l.cliente_email || '—'}</td>
                        <td>
                          <span style={getResultadoBadgeStyle(l.sucesso)}>
                            {boolResult(l.sucesso)}
                          </span>
                        </td>
                        <td title={l.motivo || ''}>{truncate(l.motivo, 90)}</td>
                        <td title={l.ip || ''}>{truncate(l.ip, 40)}</td>
                        <td title={l.user_agent || ''}>{truncate(l.user_agent, 80)}</td>
                        <td title={l.endpoint || ''}>{truncate(l.endpoint, 60)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard({ logout: logoutProp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout: authLogout } = useAuth();

  const [resumo, setResumo] = useState(null);
  const [authLogs, setAuthLogs] = useState([]);
  const [carregandoResumo, setCarregandoResumo] = useState(true);
  const [erroResumo, setErroResumo] = useState('');

  const fazerLogout = useCallback(() => {
    if (typeof logoutProp === 'function') {
      logoutProp();
    } else {
      authLogout();
    }
    navigate('/login', { replace: true });
  }, [logoutProp, authLogout, navigate]);

  const carregarResumo = useCallback(async () => {
    try {
      setCarregandoResumo(true);
      setErroResumo('');

      const [resResumo, resLogs] = await Promise.all([
        api.get('/api/dashboard/resumo-geral'),
        api.get('/api/auth-logs-recentes?limit=30'),
      ]);

      setResumo(resResumo.data || {});
      setAuthLogs(resLogs.data || []);
    } catch (e) {
      console.error('Erro ao carregar dashboard:', e);
      setErroResumo(
        e.response?.data?.message || 'Erro ao carregar a visão geral do dashboard.'
      );
    } finally {
      setCarregandoResumo(false);
    }
  }, []);

  useEffect(() => {
    carregarResumo();
  }, [carregarResumo]);

  const menu = useMemo(
    () => [
      { label: 'Visão Geral', path: '' },
      { label: 'Clientes', path: 'clientes' },
      { label: 'Assinaturas', path: 'assinaturas' },
      { label: 'Adicionar Licença', path: 'licencas/adicionar' },
      { label: 'Listar Licenças', path: 'licencas' },
      { label: 'Adicionar Pagamento', path: 'pagamentos/adicionar' },
      { label: 'Listar Pagamentos', path: 'pagamentos' },
      { label: 'Catálogo', path: 'catalogo' },
      { label: 'Resetar Senha', path: 'reset-senha' },
      { label: 'Suporte', path: 'suporte' },
    ],
    []
  );

  const isActivePath = useCallback(
    (itemPath) => {
      const current = location.pathname.replace(/\/+$/, '');
      const base = '/dashboard';

      if (!itemPath) {
        return current === base;
      }

      return current === `${base}/${itemPath}`;
    },
    [location.pathname]
  );

  return (
    <div className="dashboard-container">
      <aside className="sidebar">
        <div className="sidebar-title">Admin Quantum Trades</div>

        <div
          style={{
            fontSize: 12,
            marginBottom: 14,
            color: '#f3f4f6',
            lineHeight: 1.5,
          }}
        >
          <div>Logado como {user?.email || 'admin'}</div>
          <div>Perfil: {user?.owner || 'admin'}</div>
        </div>

        <ul className="sidebar-list">
          {menu.map((item) => (
            <li className="sidebar-item" key={item.path}>
              <button
                type="button"
                onClick={() => navigate(`/dashboard/${item.path}`)}
                style={{
                  outline: isActivePath(item.path) ? '2px solid #42a89c' : 'none',
                  boxShadow: isActivePath(item.path)
                    ? '0 0 0 2px rgba(66,168,156,0.18)'
                    : 'none',
                }}
              >
                {item.label}
              </button>
            </li>
          ))}

          <li className="sidebar-item">
            <button
              type="button"
              className="logout-button"
              onClick={fazerLogout}
            >
              Sair
            </button>
          </li>
        </ul>
      </aside>

      <main
        className="main-content"
        style={{
          background: '#f6f7fb',
          minHeight: '100vh',
          color: '#111827',
        }}
      >
        <Routes>
          <Route
            index
            element={
              <DashboardHome
                resumo={resumo}
                authLogs={authLogs}
                carregandoResumo={carregandoResumo}
                erroResumo={erroResumo}
                onReload={carregarResumo}
              />
            }
          />
          <Route path="clientes" element={<ClientesList />} />
          <Route path="assinaturas" element={<AssinaturasAdmin />} />
          <Route path="licencas/adicionar" element={<AdicionaLicencas />} />
          <Route path="licencas" element={<ListarLicencas />} />
          <Route path="pagamentos/adicionar" element={<AdicionaPagamentos />} />
          <Route path="pagamentos" element={<ListarPagamentos />} />
          <Route path="catalogo" element={<PlanosAdmin />} />
          <Route path="reset-senha" element={<ResetSenha />} />
          <Route path="suporte" element={<Suporte />} />
        </Routes>
      </main>
    </div>
  );
}